/**
 * 模块: PriceCVRInsightTab
 * 职责: 价格-转化率洞察Tab主组件
 * 限制: <280行
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Empty, Spin, Alert, Input } from 'antd';
import {
  priceCVRAPI,
  PriceElasticityResult,
  OptimalPriceRangeResult,
  PriceDisadvantageResult,
} from '../../../../services/priceCVRAPI';
import { SummaryCards } from './SummaryCards';
import { priceRangeColumns, disadvantageColumns, advantageColumns } from './tableColumns';

const { Search } = Input;

interface PriceCVRInsightTabProps {
  periodId: number | null;
}

export const PriceCVRInsightTab: React.FC<PriceCVRInsightTabProps> = ({ periodId }) => {
  const [loading, setLoading] = useState(false);
  const [elasticityData, setElasticityData] = useState<PriceElasticityResult | null>(null);
  const [priceRangeData, setPriceRangeData] = useState<OptimalPriceRangeResult | null>(null);
  const [disadvantageData, setDisadvantageData] = useState<PriceDisadvantageResult | null>(null);
  const [keywordSearch, setKeywordSearch] = useState<string>('');

  const fetchAllData = useCallback(async (searchKeyword?: string) => {
    if (!periodId) return;

    setLoading(true);
    try {
      const [elasticity, priceRange, disadvantage] = await Promise.all([
        priceCVRAPI.getPriceElasticity(periodId),
        priceCVRAPI.getOptimalPriceRange(periodId, searchKeyword),
        priceCVRAPI.getPriceDisadvantage(periodId),
      ]);

      setElasticityData(elasticity.data);
      setPriceRangeData(priceRange.data);
      setDisadvantageData(disadvantage.data);
    } catch (error: any) {
      console.error('获取价格-转化率洞察失败:', error);
    } finally {
      setLoading(false);
    }
  }, [periodId]);

  useEffect(() => {
    if (periodId) {
      fetchAllData();
    }
  }, [periodId, fetchAllData]);

  const handleSearch = (value: string) => {
    setKeywordSearch(value);
    fetchAllData(value);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 50 }}>
        <Spin size="large" tip="加载价格-转化率洞察..." />
      </div>
    );
  }

  if (!periodId || !elasticityData || !priceRangeData || !disadvantageData) {
    return <Empty description="请先选择周期查看价格-转化率洞察" />;
  }

  // 检查是否需要显示定价优化建议
  const needsPriceOptimization = priceRangeData.recommended_range !== priceRangeData.price_ranges.find(
    r => r.price_range === `$${Math.floor(priceRangeData.asin_current_price / 10) * 10}-$${Math.ceil(priceRangeData.asin_current_price / 10) * 10}`
  )?.price_range;

  return (
    <div>
      {/* 概览卡片 */}
      <SummaryCards
        elasticityData={elasticityData}
        priceRangeData={priceRangeData}
        disadvantageData={disadvantageData}
      />

      {/* 最优价格区间分析 */}
      <Card 
        title="🎯 最优价格区间分析" 
        size="small" 
        style={{ marginBottom: 16 }}
        extra={
          <Search
            placeholder="搜索关键词"
            allowClear
            onSearch={handleSearch}
            style={{ width: 250 }}
            value={keywordSearch}
            onChange={(e) => setKeywordSearch(e.target.value)}
          />
        }
      >
        {needsPriceOptimization && (
          <Alert
            message="定价优化建议"
            description={`当前定价 $${priceRangeData.asin_current_price.toFixed(2)} 不在最优区间 ${priceRangeData.recommended_range}，建议调整价格以提升转化率`}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        {priceRangeData.keyword_search && (
          <Alert
            message={`搜索结果: "${priceRangeData.keyword_search}"`}
            type="info"
            showIcon
            closable
            onClose={() => handleSearch('')}
            style={{ marginBottom: 16 }}
          />
        )}
        <Table
          dataSource={priceRangeData.price_ranges}
          columns={priceRangeColumns(priceRangeData.recommended_range)}
          pagination={false}
          size="small"
          expandable={{
            expandedRowRender: (record) => (
              <Table
                dataSource={record.keywords}
                columns={[
                  {
                    title: '关键词',
                    dataIndex: 'search_query',
                    key: 'search_query',
                    width: 200,
                  },
                  {
                    title: '搜索量',
                    dataIndex: 'search_volume',
                    key: 'search_volume',
                    render: (val: number) => val > 0 ? val.toLocaleString() : '-',
                    sorter: (a: any, b: any) => a.search_volume - b.search_volume,
                  },
                  {
                    title: '当前周期中位数价格',
                    dataIndex: ['current_period', 'median_price'],
                    key: 'current_median_price',
                    render: (val: number) => `$${val.toFixed(2)}`,
                  },
                  {
                    title: '当前周期转化率',
                    dataIndex: ['current_period', 'market_cvr'],
                    key: 'current_cvr',
                    render: (val: number) => `${val.toFixed(2)}%`,
                  },
                  {
                    title: '跨周期平均价格',
                    dataIndex: ['cross_period_summary', 'avg_median_price'],
                    key: 'avg_median_price',
                    render: (val: number) => `$${val.toFixed(2)}`,
                    sorter: (a: any, b: any) => 
                      a.cross_period_summary.avg_median_price - b.cross_period_summary.avg_median_price,
                  },
                  {
                    title: '跨周期平均转化率',
                    dataIndex: ['cross_period_summary', 'avg_market_cvr'],
                    key: 'avg_market_cvr',
                    render: (val: number) => `${val.toFixed(2)}%`,
                    sorter: (a: any, b: any) => 
                      a.cross_period_summary.avg_market_cvr - b.cross_period_summary.avg_market_cvr,
                  },
                  {
                    title: '周期数',
                    dataIndex: ['cross_period_summary', 'period_count'],
                    key: 'period_count',
                  },
                ]}
                pagination={{ pageSize: 10, size: 'small' }}
                size="small"
                scroll={{ x: 1200 }}
              />
            ),
            rowExpandable: (record) => record.keywords && record.keywords.length > 0,
          }}
        />
      </Card>

      {/* 价格劣势关键词 */}
      <Card
        title="⚠️ 价格劣势关键词（需要优化）"
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Table
          dataSource={disadvantageData.disadvantage_keywords}
          columns={disadvantageColumns}
          pagination={{ pageSize: 10 }}
          size="small"
          scroll={{ y: 300 }}
        />
      </Card>

      {/* 价格优势关键词 */}
      <Card title="✅ 价格优势关键词（表现良好）" size="small">
        <Table
          dataSource={disadvantageData.advantage_keywords}
          columns={advantageColumns}
          pagination={{ pageSize: 5 }}
          size="small"
          scroll={{ y: 200 }}
        />
      </Card>
    </div>
  );
};

